import { ArrowRightIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { Link } from '@remix-run/react';
import { GQLCategoryFragment } from '@theprintbar/graphql';
import { ImageSize, WebRoutes, cn, sizedImage, useDebounce, useOutsideClick } from '@theprintbar/shared';
import { PromoImageContainer } from '@theprintbar/ui';
import { useEffect, useId, useRef, useState } from 'react';

interface CategoryDropdownProps {
	category: GQLCategoryFragment;
	categories: GQLCategoryFragment[];
	activeCategory?: string;
	setActiveCategory?: (code: string) => void;
	navRef: React.RefObject<HTMLDivElement>;
}

export function CategoryDropdown({ category, categories, navRef, activeCategory, setActiveCategory }: CategoryDropdownProps) {
	const buttonRef = useRef<HTMLButtonElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const [open, setOpen] = useState(false);
	const [debouncedOpen, setDebouncedOpen] = useDebounce(open, 100, { onlyDebounceWhenFalse: true });
	const [usingKeyboard, setUsingKeyboard] = useState(false);
	useOutsideClick(navRef, () => setOpen(false));

	useEffect(() => {
		if (!debouncedOpen) return;
		setActiveCategory?.(category.code);
	}, [debouncedOpen]);

	useEffect(() => {
		if (activeCategory !== category.code) {
			setOpen(false);
		}
	}, [activeCategory]);

	// keyboard modality + escape
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (['Tab', 'ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.key)) setUsingKeyboard(true);
			if (e.key === 'Escape') setOpen(false);
		};
		const onMouse = () => setUsingKeyboard(false);
		window.addEventListener('keydown', onKey);
		window.addEventListener('mousedown', onMouse);
		return () => {
			window.removeEventListener('keydown', onKey);
			window.removeEventListener('mousedown', onMouse);
		};
	}, []);

	useEffect(() => {
		const el = panelRef.current;
		if (!el) return;
		if (open) {
			el.removeAttribute('inert');
		} else {
			el.setAttribute('inert', '');
		}
	}, [open]);

	// open on desktop hover/focus, but keep content always in DOM for SEO
	const onMouseEnter = () => setOpen(true);
	const onMouseLeave = () => setOpen(false);

	// move focus to first link when opening via keyboard
	function openAndFocusFirst() {
		setOpen(true);
		requestAnimationFrame(() => {
			const first = panelRef.current?.querySelector(`a`);
			(first as HTMLElement | null)?.focus();
		});
	}

	function onTriggerKeyDown(e: React.KeyboardEvent) {
		switch (e.key) {
			case 'ArrowDown':
			case 'Enter':
			case ' ': // space
				e.preventDefault();
				if (!debouncedOpen) openAndFocusFirst();
				else setOpen(false);
				break;
		}
	}

	const childlessCategories = categories.filter(
		cat => cat.parents?.find(parent => parent.code === category.code) && !cat.children?.length,
	);
	const nestedCategories = categories.filter(cat => cat.parents?.find(parent => parent.code === category.code) && !!cat.children?.length);
	const categoryItems = [category, ...nestedCategories];

	return (
		<div
			className={cn('h-full hidden md:block transition duration-150 ease-in-out', debouncedOpen && 'bg-blue/10')}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
		>
			<button
				ref={buttonRef}
				aria-haspopup="true"
				aria-expanded={debouncedOpen}
				aria-controls={`panel-${category.code}`}
				onKeyDown={onTriggerKeyDown}
				onFocus={openAndFocusFirst}
				className={cn(
					'h-full inline-flex transition-all font-semibold font-belfast text-sm items-center gap-1 rounded-lg px-2',
					usingKeyboard && 'focus:outline-none focus-visible:underline',
				)}
			>
				{category.name.toUpperCase()}
				<ChevronUpIcon className={cn('w-4 h-4 transition duration-300', debouncedOpen ? 'rotate-0' : 'rotate-180')} />
			</button>

			{/* Panel: always in DOM, visibility via CSS only */}
			<div
				ref={panelRef}
				id={`panel-${category.code}`}
				role="region"
				aria-label={`${category.name} menu`}
				aria-hidden={!debouncedOpen}
				className={cn(
					'transition absolute inset-x-0 top-full z-40',
					'border-y border-blue bg-white-pearl shadow-lg',
					debouncedOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
				)}
			>
				<div className="grid grid-cols-3 gap-12 p-6 w-full max-w-5xl mx-auto">
					{categoryItems.map((cat, ci) => {
						return (
							<div key={ci} className="flex flex-col w-full">
								{cat.name && (
									<a href={WebRoutes.Category(cat.slug)}>
										<h5 className="uppercase text-sm hover:underline">
											{cat.code === category.code && 'All '}
											{cat.name}
										</h5>
									</a>
								)}
								<ul className="[&>li::marker]:content-[''] text-sm mt-2 flex-1">
									{categories
										.filter(c => c.parents?.some(p => p.code === cat.code))
										.map((l, li) => (
											<li key={li} className="py-2">
												<a href={WebRoutes.Category(l.slug)} className="hover:underline py-2">
													{l.name}
												</a>
											</li>
										))}
								</ul>
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
}
